import { Css } from "@homebound/beam";
import { Navigate, Route, Routes } from "react-router-dom";
import {
  createBidPackagesUrl,
  createBudgetMetricsUrl,
  createBudgetUrl,
  createChangeEventsUrl,
  createCommitmentsUrl,
  createDocumentsUrl,
  createDraftScheduleUrl,
  createEstimatesUrl,
  createHomeownerContractsUrl,
  createInvoicesUrl,
  createPreConServicesUrl,
  createProductOfferingsUrl,
  createProjectBidContractsUrl,
  createProjectChangeLogUrl,
  createProjectDashboardUrl,
  createProjectDropsUrl,
  createProjectExpensesUrl,
  createProjectHomeownerNotesUrl,
  createProjectJobLogsUrl,
  createProjectLotSummaryUrl,
  createProjectScheduleUrl,
  createProjectSettingsUrl,
  createProjectToDosUrl,
  createSpecsAndSelectionsUrl,
} from "src/RouteUrls";
import { CoverImage, Icon } from "src/components";
import {
  SideNavAndRouterConfig,
  SideNavAndRouterConfigItem,
  SideNavDisplayStyle,
  SideNavProps,
  navLinksToRoutes,
} from "src/components/layout/SideNav";
import { SideNavLayout } from "src/components/layout/SideNavLayout";
import { useFeatureFlags } from "src/contexts/FeatureFlags/FeatureFlagContext";
import { FeatureFlagType, LotTypeDetail, ProjectFeature, Stage } from "src/generated/graphqlTypes";
import { useRouteParams } from "src/hooks/useRouteParams";
import { NotFound } from "src/routes/NotFound";
import { ProjectLayoutFragment, useProjectLayoutQuery } from "src/routes/projects/ProjectLayout.generated";
import { ChangeEventPage } from "src/routes/projects/change-events/ChangeEventPage";
import { ChangeEventsPage } from "src/routes/projects/change-events/ChangeEventsPage";
import { CommitmentChangeOrderPage } from "src/routes/projects/commitments/CommitmentChangeOrderPage";
import { ProjectContextProvider } from "src/routes/projects/context/ProjectContext";
import { DocumentsPage } from "src/routes/projects/documents/DocumentsPage";
import { EstimatePage } from "src/routes/projects/estimates/EstimatePage";
import { ProjectEstimatesPage } from "src/routes/projects/estimates/ProjectEstimatesPage";
import { ExpensesPage } from "src/routes/projects/expenses/ExpensesPage";
import { HomeownerContractChangeOrderPage } from "src/routes/projects/homeowner-contracts/HomeownerContractChangeOrderPage";
import { HomeownerContractPage } from "src/routes/projects/homeowner-contracts/HomeownerContractPage";
import { HomeownerContractsPage } from "src/routes/projects/homeowner-contracts/HomeownerContractsPage";
import { HomeownerNotesPage } from "src/routes/projects/homeowner-notes/HomeownerNotesPage";
import { ProjectInvoicesPage } from "src/routes/projects/invoices/ProjectInvoicesPage";
import { ProjectItemsPage } from "src/routes/projects/items/ProjectItemsPage";
import { JobLogsPage } from "src/routes/projects/job-logs/JobLogsPage";
import { ProjectSettingsPage } from "src/routes/projects/settings/ProjectSettingsPage";
import { ToDosPage } from "src/routes/projects/to-dos/ToDosPage";
import { assertNever } from "src/utils";
import { getProjectTypeIcon } from "src/utils/projects";
import { queryResult } from "src/utils/queryResult";
import { ProjectBidPackagesPage } from "../bids/BidPackagesPage";
import { BillPage } from "../bills/BillPage";
import { ProductOfferingConfigsComparePage } from "../developments/product-offering-configs/ProductOfferingConfigsComparePage";
import { ProductOfferingApplyToProjectsPage } from "../developments/product-offerings/ProductOfferingApplyToProjectsPage";
import { ProductOfferingPage } from "../developments/product-offerings/ProductOfferingPage";
import { ProductOfferingsPage } from "../developments/product-offerings/ProductOfferingsPage";
import { ProductOfferingVersionHistoryPage } from "../developments/product-offerings/version-history/ProductOfferingVersionHistoryPage";
import { ProjectParams, dynamicSchedulesPath, projectPaths, relativeProjectPath } from "../routesDef";
import { ProjectBidContractPage } from "./bid-contracts/ProjectBidContractPage";
import { ProjectBidContractsPage } from "./bid-contracts/ProjectBidContractsPage";
import { BudgetMetricsPage } from "./budget/BudgetMetricsPage";
import { BudgetPage } from "./budget/BudgetPage";
import { HierarchyBudgetPage } from "./budget/HierarchyBudgetPage";
import { ProjectChangeLogPage } from "./change-log/ProjectChangeLogPage";
import { CommitmentPage } from "./commitments/CommitmentPage";
import { CommitmentsPage } from "./commitments/CommitmentsPage";
import { RedirectToActiveStage } from "./components/RedirectToActiveStage";
import { DashboardSwitcherPage } from "./dashboard";
import { ProjectDropsPage } from "./drops/ProjectDropsPage";
import { ScheduleDraftMode } from "./dynamic-schedules/draft-mode/ScheduleDraftMode";
import { MilestoneDetailsPage } from "./dynamic-schedules/milestone-view/MilestoneDetailsPage";
import { TaskDetailsPage } from "./dynamic-schedules/task-details/TaskDetailsPage";
import { TaskMaterialsPage } from "./dynamic-schedules/task-details/TaskMaterialsPage";
import { ProjectFinishSchedulePage } from "./finish-schedule/ProjectFinishSchedulePage";
import { InvoicePage, InvoicePageOld } from "./invoices/InvoicePage";
import InvoicePageV2 from "./invoices/invoice-v2/InvoicePageV2";
import { ProjectLotSummaryPage } from "./lot-summary/ProjectLotSummaryPage";
import { AppliableProductOfferingConfigsPage } from "./product-offering-configs-apply/AppliableProductOfferingConfigsPage";
import { ApplyProductOfferingConfigPage } from "./product-offering-configs-apply/ApplyProductOfferingConfigPage";
import { ProjectProductOfferingConfigsPage } from "./product-offering-configs/ProjectProductOfferingConfigsPage";
import { ProjectSchedulePageV2 } from "./schedule-v2/ProjectSchedulePageV2";
import { SpecsAndSelectionsRouteHandler } from "./selections/SpecsAndSelectionsPage";

export function ProjectLayout() {
  const { projectId } = useRouteParams<ProjectParams>();
  const query = useProjectLayoutQuery({ variables: { projectId } });

  return queryResult(query, {
    data: ({ project }) => {
      const {
        lotType,
        hopUrl,
        features,
        name,
        client,
        buildAddress,
        coverImage,
        status,
        market,
        cohort,
        featureFlags,
        latestActiveStage,
        communityType,
        __typename,
        sellableProjects,
        parentsRecursive,
        siblingProjects,
      } = project;

      return (
        <ProjectContextProvider
          key={projectId}
          id={projectId}
          client={client}
          buildAddress={buildAddress ?? undefined}
          name={name}
          features={features}
          latestActiveStage={latestActiveStage?.stage.code ?? Stage.Construction}
          lotType={lotType?.code}
          hopUrl={hopUrl}
          propcoUrl={"propcoUrl" in project ? project.propcoUrl : null}
          communityType={communityType?.code}
          coverImage={coverImage}
          isSet={true}
          status={status}
          market={market ?? undefined}
          cohort={cohort ?? undefined}
          development={cohort?.development ?? undefined}
          featureFlags={featureFlags.map((f) => f.type.code)}
          canHaveBudget={project.canHaveBudget.allowed}
          sellableChildren={sellableProjects}
          parentsRecursive={parentsRecursive}
          siblingProjects={siblingProjects}
          {...lotType}
          __typename={__typename}
        >
          <ProjectLayoutContent project={project} />
        </ProjectContextProvider>
      );
    },
    /**
     * Due to the default behavior of `queryResult` whenever we were at the project A's schedule page and we changed to another project B's,
     * `queryResult` would show still show project A's schedule page until the query for project B's schedule page completed.
     * This causes `RedirectToActiveStage` to redirect to project A's schedule page instead of project B's.
     *
     * with `showLoading: "always"` we are forcing `queryResult` to always show the loading state until the query for project B's schedule page completes.
     *
     */
    showLoading: "always",
  });
}

type ProjectLayoutContentProps = { project: ProjectLayoutFragment };

function ProjectLayoutContent({ project }: ProjectLayoutContentProps) {
  const { id, lotType, features, name, buildAddress, __typename, hopUrl } = project;
  const { featureIsEnabled } = useFeatureFlags();
  const { city, state, postalCode } = buildAddress ?? {};

  const sideNavProps: SideNavProps =
    __typename === "StandaloneProject"
      ? {
          sideNavConfig: standaloneProjectNavLinks(
            lotType,
            id,
            project.latestActiveStage?.stage.code ?? Stage.Construction,
            features,
            hopUrl,
          ),
          top: (
            <div css={Css.pb5.$}>
              <CoverImage />
            </div>
          ),
        }
      : {
          sideNavConfig: hierarchyNavLinks(project),
          top: (
            <div css={Css.pl2.pt3.pb4.$}>
              <div css={Css.dif.aic.br4.bgGray200.gray600.hPx(40).pl1.pr2.gap1.$}>
                <Icon icon={getProjectTypeIcon(project)} />
                <div css={Css.smSb.$}>{__typename}</div>
              </div>
              <div css={Css.df.fdc.pt1.$}>
                <div css={Css.lg.pr2.ptPx(5).pb1.lhPx(20).$}>{name}</div>
                {buildAddress && (
                  <span css={Css.xs.$}>{`${city}, ${state} ${__typename !== "Community" ? postalCode : ""}`}</span>
                )}
              </div>
            </div>
          ),
          displayStyle: SideNavDisplayStyle.Collapsible,
          storagekey: "projectLayout",
        };

  const rp = relativeProjectPath;

  return (
    <SideNavLayout sideNavProps={sideNavProps}>
      <Routes>
        <Route path={`${rp(projectPaths.settings)}/*`} element={<ProjectSettingsPage />} />
        <Route path={rp(projectPaths.contractChangeOrder)} element={<HomeownerContractChangeOrderPage />} />
        <Route path={rp(projectPaths.contract)} element={<HomeownerContractPage />} />
        <Route path={rp(projectPaths.contracts)} element={<HomeownerContractsPage />} />
        <Route path={rp(projectPaths.commitmentChangeOrder)} element={<CommitmentChangeOrderPage />} />
        <Route path={rp(projectPaths.bill)} element={<BillPage />} />
        <Route path={rp(projectPaths.commitment)} element={<CommitmentPage />} />
        <Route
          path={`${rp(projectPaths.invoice)}/*`}
          element={features.includes(ProjectFeature.InvoiceV2) ? <InvoicePageV2 /> : <InvoicePageOld />}
        />
        <Route
          path={`${rp(projectPaths.invoiceOld)}/*`}
          element={features.includes(ProjectFeature.InvoiceV2) ? <InvoicePage /> : <InvoicePageOld />}
        />
        <Route path={rp(projectPaths.estimate)} element={<EstimatePage />} />
        <Route
          path={rp(projectPaths.scheduleBase)}
          element={
            <RedirectToActiveStage
              showPlanning
              projectId={id}
              createUrl={(stage) => createProjectScheduleUrl(id, stage)}
            />
          }
        />
        <Route path={rp(projectPaths.items)} element={<ProjectItemsPage />} />
        <Route path={`${rp(dynamicSchedulesPath.draftMode)}/*`} element={<ScheduleDraftMode />} />
        <Route path={rp(dynamicSchedulesPath.details)} element={<TaskDetailsPage />} />
        <Route path={rp(dynamicSchedulesPath.materials)} element={<TaskMaterialsPage />} />
        <Route path={rp(dynamicSchedulesPath.milestoneDetails)} element={<MilestoneDetailsPage />} />
        <Route
          path={rp(projectPaths.dynamicSchedules)}
          element={<Navigate to={dynamicSchedulesPath.draftMode} replace />}
        />

        <Route path={rp(projectPaths.productOfferingVersionHistory)} element={<ProductOfferingVersionHistoryPage />} />
        <Route path={rp(projectPaths.productOfferingSpecificConfigs)} element={<ProjectProductOfferingConfigsPage />} />
        <Route path={rp(projectPaths.productOffering)} element={<ProductOfferingPage />} />
        <Route path={rp(projectPaths.productOfferings)} element={<ProductOfferingsPage />} />
        <Route path={rp(projectPaths.productOfferingConfigs)} element={<ProjectProductOfferingConfigsPage />} />
        <Route
          path={rp(projectPaths.productOfferingSpecificConfigsCompare)}
          element={<ProductOfferingConfigsComparePage />}
        />
        <Route path={rp(projectPaths.productOfferingConfigsCompare)} element={<ProductOfferingConfigsComparePage />} />
        <Route path={rp(projectPaths.productOfferingApplyToProject)} element={<ProductOfferingApplyToProjectsPage />} />
        <Route
          path={rp(projectPaths.productOfferingConfigAppliable)}
          element={<AppliableProductOfferingConfigsPage />}
        />
        <Route path={rp(projectPaths.productOfferingConfigApply)} element={<ApplyProductOfferingConfigPage />} />

        <Route path={rp(projectPaths.bidContracts)} element={<ProjectBidContractsPage />} />
        <Route path={rp(projectPaths.bidContractOverview)} element={<ProjectBidContractPage />} />
        <Route path={rp(projectPaths.bidContractPlanPricing)} element={<ProjectBidContractPage />} />
        <Route path={rp(projectPaths.bidContractUnitPricing)} element={<ProjectBidContractPage />} />
        <Route path={rp(projectPaths.bidContractPurchaseOrders)} element={<ProjectBidContractPage />} />
        <Route path={rp(projectPaths.bidContract)} element={<Navigate to="overview" replace relative="path" />} />
        <Route
          path={rp(projectPaths.selectionsBase)}
          element={<Navigate to={projectPaths.specsAndSelections} replace />}
        />
        <Route
          path={rp(projectPaths.selectionsPreCon)}
          element={<Navigate to={projectPaths.preConServices} replace />}
        />
        <Route
          path={rp(projectPaths.selectionsPreConDetails)}
          element={<Navigate to={projectPaths.preConServicesDetail} replace />}
        />
        <Route
          path={rp(projectPaths.selectionsConstruction)}
          element={<Navigate to={projectPaths.specsAndSelections} replace />}
        />
        <Route
          path={rp(projectPaths.selectionsConstructionDetails)}
          element={<Navigate to={projectPaths.specsAndSelectionsDetail} replace />}
        />

        <Route
          path={`${rp(projectPaths.preConServices)}/*`}
          element={<SpecsAndSelectionsRouteHandler stage={Stage.PreConExecution} />}
        />

        <Route path={rp(projectPaths.lotSummary)} element={<ProjectLotSummaryPage />} />
        <Route path={rp(projectPaths.finishSchedule)} element={<ProjectFinishSchedulePage />} />

        <Route
          path={`${rp(projectPaths.specsAndSelections)}/*`}
          element={<SpecsAndSelectionsRouteHandler stage={Stage.Construction} />}
        />
        <Route path={`${rp(projectPaths.changeEvent)}/*`} element={<ChangeEventPage />} />
        <Route path={`${rp(projectPaths.dashboard)}/*`} element={<DashboardSwitcherPage />} />

        <Route path={rp(projectPaths.expenses)} element={<ExpensesPage />} />

        {navLinksToRoutes(sideNavProps.sideNavConfig, featureIsEnabled, rp)}

        <Route path="" element={<Navigate to={createProjectDashboardUrl(id)} replace />} />
        <Route
          path={rp(projectPaths.budgetMetrics)}
          element={<Navigate to={createProjectDashboardUrl(id)} replace />}
        />
        <Route path={rp(projectPaths.budgetBase)} element={<Navigate to={createProjectDashboardUrl(id)} replace />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </SideNavLayout>
  );
}

function standaloneProjectNavLinks(
  lotType: Pick<LotTypeDetail, "shortClientNoun" | "clientContract" | "clientInvoice">,
  projectId: string,
  stage: Stage,
  features: ProjectFeature[] = [],
  hopUrl: string,
): SideNavAndRouterConfig[] {
  return [
    {
      title: "Project",
      items: [
        {
          label: "Dashboard",
          component: DashboardSwitcherPage,
          path: projectPaths.dashboard,
          href: createProjectDashboardUrl(projectId),
        },
        {
          label: "Schedule",
          component: features.includes(ProjectFeature.ProductConfigPlan) ? ScheduleDraftMode : ProjectSchedulePageV2,
          path: features.includes(ProjectFeature.ProductConfigPlan)
            ? dynamicSchedulesPath.draftMode
            : projectPaths.schedule,
          href: features.includes(ProjectFeature.ProductConfigPlan)
            ? createDraftScheduleUrl(projectId)
            : createProjectScheduleUrl(projectId, stage),
        },
        {
          label: "Job Logs",
          component: JobLogsPage,
          path: `${projectPaths.jobLogs}/*`,
          href: createProjectJobLogsUrl(projectId),
        },
        {
          label: "To-Dos",
          component: ToDosPage,
          path: `${projectPaths.toDos}/*`,
          href: createProjectToDosUrl(projectId),
        },
        ...(!features.includes(ProjectFeature.ProductConfigPlan)
          ? [{ label: "Pre-Con Services", path: projectPaths.preConServices, href: createPreConServicesUrl(projectId) }]
          : []),
        { label: "Lot Summary ", path: projectPaths.lotSummary, href: createProjectLotSummaryUrl(projectId) },
        {
          label: "Specs & Selections",
          path: projectPaths.specsAndSelections,
          href: createSpecsAndSelectionsUrl(projectId),
        },
        {
          label: "Change Events",
          component: ChangeEventsPage,
          path: projectPaths.changeEvents,
          href: createChangeEventsUrl(projectId),
        },
        {
          label: "Change Log",
          path: `${projectPaths.changeLog}/*`,
          href: createProjectChangeLogUrl(projectId),
          component: ProjectChangeLogPage,
          featureFlag: FeatureFlagType.ChangeRequestMvp,
        },
        {
          label: "Documents",
          component: DocumentsPage,
          path: projectPaths.documents,
          href: createDocumentsUrl(projectId),
        },
        {
          label: `H/O Notes`,
          component: HomeownerNotesPage,
          path: projectPaths.homeownerNotes,
          href: createProjectHomeownerNotesUrl(projectId),
        },
      ],
    },
    {
      title: "Finances",
      items: [
        { label: "Budget", component: BudgetPage, path: projectPaths.budgetBase, href: createBudgetUrl(projectId) },
        {
          label: "Estimates",
          component: ProjectEstimatesPage,
          path: projectPaths.estimates,
          href: createEstimatesUrl(projectId),
        },
        {
          label: lotType.clientContract.includes("Contract") ? "Contracts" : `${lotType.clientContract}s`,
          path: projectPaths.contracts,
          href: createHomeownerContractsUrl(projectId),
        },
        {
          label: `${lotType.clientInvoice}s`,
          component: ProjectInvoicesPage,
          path: projectPaths.invoices,
          href: createInvoicesUrl(projectId),
        },
        {
          label: "Trade Commitments",
          component: CommitmentsPage,
          path: [projectPaths.commitments, projectPaths.bill],
          href: createCommitmentsUrl(projectId),
        },
        { label: "Trade Expenses", component: ExpensesPage, path: [], href: createProjectExpensesUrl(projectId) },
      ],
    },
    {
      items: [
        { label: "H/O Platform", href: hopUrl, isExternal: true },
        { label: "Settings", path: projectPaths.settings, href: createProjectSettingsUrl(projectId) },
      ],
    },
  ];
}

type ProjectLayoutItem = Omit<SideNavAndRouterConfigItem, "href"> & { href: (projectId: string) => string };
type ProjectLayoutSection = Omit<SideNavAndRouterConfig, "items"> & { items: ProjectLayoutItem[] };
type HierarchyProjectLayoutFragment = Extract<
  ProjectLayoutFragment,
  { __typename: Exclude<ProjectLayoutFragment["__typename"], "StandaloneProject"> }
>;

const allSections: Record<string, ProjectLayoutSection> = {
  dashboard: {
    items: [
      {
        label: "Dashboard",
        component: DashboardSwitcherPage,
        path: projectPaths.dashboard,
        href: (projectId) => createProjectDashboardUrl(projectId),
      },
    ],
  },
  schedule: {
    title: "Schedule",
    items: [
      // TODO: Add Metrics tab
      {
        label: "Schedule",
        component: ScheduleDraftMode,
        path: dynamicSchedulesPath.draftMode,
        href: (projectId) => createDraftScheduleUrl(projectId),
      },
      {
        label: "Job Logs",
        component: JobLogsPage,
        path: `${projectPaths.jobLogs}/*`,
        href: (projectId) => createProjectJobLogsUrl(projectId),
      },
    ],
  },
  budgets: {
    title: "Budgets",
    items: [
      {
        label: "Metrics",
        component: BudgetMetricsPage,
        path: projectPaths.budgetMetrics,
        href: (projectId) => createBudgetMetricsUrl(projectId),
      },
      {
        label: "Budget",
        component: HierarchyBudgetPage,
        path: projectPaths.budgetBase,
        href: (projectId) => createBudgetUrl(projectId),
      },
      {
        label: "POs",
        component: CommitmentsPage,
        path: [projectPaths.commitments, projectPaths.bill],
        href: (projectId) => createCommitmentsUrl(projectId),
      },
      {
        label: "Change Events",
        component: ChangeEventsPage,
        path: projectPaths.changeEvents,
        href: (projectId) => createChangeEventsUrl(projectId),
      },
      { label: "Expenses", component: ExpensesPage, href: (projectId) => createProjectExpensesUrl(projectId) },
      {
        label: "Estimates",
        component: ProjectEstimatesPage,
        path: projectPaths.estimates,
        href: (projectId) => createEstimatesUrl(projectId),
      },
      {
        label: "Client Contracts",
        path: projectPaths.contracts,
        href: (projectId) => createHomeownerContractsUrl(projectId),
      },
      {
        label: "Client Invoices",
        component: ProjectInvoicesPage,
        path: projectPaths.invoices,
        href: (projectId) => createInvoicesUrl(projectId),
      },
    ],
  },
  bids: {
    title: "Bids",
    items: [
      // TODO: Add Metrics tab
      {
        label: `Product Offerings`,
        component: ProductOfferingsPage,
        path: projectPaths.productOfferings,
        href: (projectId) => createProductOfferingsUrl(undefined, projectId),
      },
      {
        label: "Drops",
        component: ProjectDropsPage,
        path: projectPaths.drops,
        href: (projectId) => createProjectDropsUrl(projectId),
      },
      {
        label: `Bid Packages`,
        component: ProjectBidPackagesPage,
        path: projectPaths.bidPackages,
        href: (projectId) => createBidPackagesUrl(projectId),
      },
      {
        label: "Bid Contracts",
        component: ProjectBidContractsPage,
        path: projectPaths.bidContracts,
        href: (projectId) => createProjectBidContractsUrl(projectId),
      },
    ],
  },
  scope: {
    items: [
      {
        label: "Scope",
        path: projectPaths.specsAndSelections,
        href: (projectId) => createSpecsAndSelectionsUrl(projectId),
      },
    ],
  },
  lotSummary: { items: [{ label: "Lot Summary", href: (projectId) => createProjectLotSummaryUrl(projectId) }] },
  documents: {
    items: [
      {
        label: "Documents",
        component: DocumentsPage,
        path: projectPaths.documents,
        href: (projectId) => createDocumentsUrl(projectId),
      },
    ],
  },
  settings: {
    pinnedToBottom: true,
    items: [
      { label: "Settings", path: projectPaths.settings, href: (projectId) => createProjectSettingsUrl(projectId) },
    ],
  },
};

const { dashboard, schedule, budgets, bids, scope, lotSummary, documents, settings } = allSections;
const [directSchedule] = schedule.items;
const [budgetMetrics, budget, commitments, changeEvents, expenses, estimates, clientContracts, clientInvoices] =
  budgets.items;
const [productOfferings, drops, bidPackages, bidContracts] = bids.items;

function hierarchyNavLinks(project: HierarchyProjectLayoutFragment): SideNavAndRouterConfig[] {
  const { id, canHaveBudget, hopUrl } = project;
  let navLinks: ProjectLayoutSection[];

  switch (project.__typename) {
    case "Region":
      navLinks = [
        dashboard,
        // TODO: include budget reports item as a standalone section
        documents,
        settings,
      ];
      break;
    case "Market":
      navLinks = [
        dashboard,
        // TODO: include budget reports item as a standalone section
        { ...bids, items: [bidPackages, bidContracts] },
        documents,
        settings,
      ];
      break;
    case "Community":
      navLinks = [
        dashboard,
        { ...schedule, items: [directSchedule] },
        {
          ...budgets,
          items: !canHaveBudget.allowed
            ? [{ ...budgetMetrics, label: "Budgets" }]
            : [budgetMetrics, budget, commitments, changeEvents, expenses, estimates, clientContracts],
        },
        { ...bids, items: [productOfferings, drops, bidPackages, bidContracts] },
        ...(!canHaveBudget.allowed ? [] : [scope]),
        documents,
        settings,
      ];
      break;

    case "Subdivision":
      navLinks = [
        dashboard,
        { ...schedule, items: [directSchedule] },
        {
          ...budgets,
          items: !canHaveBudget.allowed
            ? [{ ...budgetMetrics, label: "Budgets" }]
            : [budgetMetrics, budget, commitments, changeEvents, expenses, estimates, clientContracts],
        },
        { ...bids, items: [productOfferings, drops, bidPackages, bidContracts] },
        ...(!canHaveBudget.allowed ? [] : [scope]),
        documents,
        settings,
      ];
      break;

    case "Lot":
      navLinks = [
        dashboard,
        schedule,
        {
          ...budgets,
          items: [
            budgetMetrics,
            budget,
            commitments,
            changeEvents,
            expenses,
            estimates,
            clientContracts,
            clientInvoices,
          ],
        },
        scope,
        documents,
        settings,
      ];
      break;

    case "Structure":
    case "Unit":
      navLinks = [
        dashboard,
        schedule,
        budgets,
        scope,
        lotSummary,
        documents,
        {
          title: "Homeowner",
          items: [
            {
              label: "H/O Notes",
              component: HomeownerNotesPage,
              path: projectPaths.homeownerNotes,
              href: (projectId) => createProjectHomeownerNotesUrl(projectId),
            },
            { label: "H/O Platform", href: () => hopUrl, isExternal: true },
          ],
        },
        settings,
      ];
      break;

    default:
      assertNever(project);
  }

  return navLinks.map((section) => ({
    ...section,
    items: section.items.map((item) => ({
      ...item,
      // call our href helper
      href: item.href(id),
    })),
  }));
}
